import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import { Database } from '@/_constants/database.types';

type PracticeSet = Database['public']['Tables']['practice_attempts']['Row'];
type PracticeAnswer = Database['public']['Tables']['practice_answers']['Row'];
type Question = Database['public']['Tables']['questions']['Row'];

export interface PracticeAnswerWithQuestion extends PracticeAnswer {
  questions: Pick<
    Question,
    | 'id'
    | 'content'
    | 'marks'
    | 'category'
    | 'category_code'
    | 'image_page_numbers'
    | 'mark_scheme_content'
    | 'model_answer'
    | 'hint_content'
    | 'pdf_url'
    | 'reading_insert_content'
    | 'reading_insert_content_2'
    | 'reading_insert_page_numbers'
    | 'has_image'
  >;
}

export interface PracticeSetWithDetails extends PracticeSet {
  practice_answers: PracticeAnswerWithQuestion[];
}

const usePracticeSet = (practiceSetId: string | undefined) => {
  return useQuery({
    queryKey: ['practice_set', practiceSetId],
    queryFn: async (): Promise<PracticeSetWithDetails | null> => {
      if (!practiceSetId) return null;

      const { data, error } = await supabaseClient
        .from('practice_attempts')
        .select(
          `
          *,
          practice_answers (
            *,
            questions (
              id,
              content,
              marks,
              category,
              category_code,
              image_page_numbers,
              mark_scheme_content,
              model_answer,
              hint_content,
              pdf_url,
              reading_insert_content,
              reading_insert_content_2,
              reading_insert_page_numbers,
              has_image
            )
          )
        `
        )
        .eq('id', practiceSetId)
        .order('question_order', { referencedTable: 'practice_answers', ascending: true })
        .single();

      if (error) throw error;

      return data as PracticeSetWithDetails;
    },
    enabled: !!practiceSetId,
  });
};

export default usePracticeSet;
